import {
  formatWhatsAppUrl,
  splitPhoneNumberIntoParts,
  useModal,
  useRouter,
} from '@application/utils';
import { WhatsappModal } from '@presentation/components';
import type { WhatsappModalFormValues } from '@presentation/components';
import { isProduction } from '@application/env';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { Analytics } from '@application/utils/analytics';
import { FormikProvider, useFormik } from 'formik';
import { addWhatsappData, useCurrentAccount } from '@application/api';
import { useGetPro } from './api/useGetPro';
import { useFormatPro } from './api/useFormatPro';
import { useValidationSchema } from './useValidationSchema';
import { useSaveEmailContact } from './api/useSaveEmailContact';
import { useGetBreeder } from './api/useGetBreeder';
import { useFormatBreeder } from './api/useFormatBreeder';

const init: ReturnType<typeof useWhatsappModalSingleton> = {
  proId: undefined,
  breederId: undefined,
  modalHook: {
    show: () => undefined,
    showFromClick: () => undefined,
    close: () => undefined,
    modal: {
      onCloseClick: undefined,
      open: false,
      position: [0, 0],
    },
  },
};

let globalProId: Dispatch<SetStateAction<number | undefined>>;
let globalBreederId: Dispatch<SetStateAction<number | undefined>>;

export const useWhatsappModalSingleton = () => {
  const modalHook = useModal({});
  const [proId, setProId] = useState<number>();
  const [breederId, setBreederId] = useState<number>();
  globalProId = setProId;
  globalBreederId = setBreederId;

  return {
    modalHook,
    proId,
    breederId,
  };
};

export const setProId = (proId: number) => {
  globalProId(proId);
  globalBreederId(undefined);
};
export const setWhatsappBreederId = (breederId: number) => {
  globalBreederId(breederId);
  globalProId(undefined);
};

export const useWhatsappModal = singletonHook(init, useWhatsappModalSingleton);

export const WhatsappModalController: FC = () => {
  const { modalHook, proId, breederId } = useWhatsappModal();
  const { translate, country } = useRouter();
  const { pro } = useGetPro(proId);
  const { breeder, breederFilteredBreeds } = useGetBreeder(breederId);
  const { formattedPro } = useFormatPro(pro);
  const { formattedBreeder } = useFormatBreeder(breeder, breederFilteredBreeds);
  const { currentAccount } = useCurrentAccount();
  const { createEmailContact } = useSaveEmailContact();

  const isPro = !!pro;
  const onSubmitHandler = useCallback(
    (values: WhatsappModalFormValues) => {
      const resource = isPro ? 'pro' : 'breeder';

      // TODO: REMOVE THIS ONCE WE NO LONGER NEED IT
      if ((proId || breederId) && isProduction()) {
        const id = (proId ?? breederId) as number;
        addWhatsappData(id, values, resource)
          .then(() => {})
          .catch((err) => console.log(err));
      }

      createEmailContact(values, resource);
      if (isPro) {
        Analytics.confirmClickOnGroomerWhatsapp();
      } else {
        Analytics.confirmClickOnBreederWhatsapp();
      }

      const number = (isPro ? pro?.whatsappNumber : breeder?.whatsappNumber) as string;
      const targetUrl = formatWhatsAppUrl({
        values,
        number,
        translate,
        resource,
      });

      window.open(targetUrl, '_blank');
      modalHook.close();
    },
    [
      breeder?.whatsappNumber,
      breederId,
      createEmailContact,
      isPro,
      modalHook,
      pro?.whatsappNumber,
      proId,
      translate,
    ],
  );
  const { validationSchema } = useValidationSchema();

  const initialValues: WhatsappModalFormValues = useMemo(() => {
    const values = {
      email: currentAccount?.email ?? '',
      firstName: currentAccount?.firstName ?? '',
      lastName: currentAccount?.lastName ?? '',
      message: undefined,
      marketingEmailConsent: false,
      phone: '',
      phonePrefixCountry: country.toUpperCase(),
    };
    if (currentAccount?.phone) {
      const splits = splitPhoneNumberIntoParts(currentAccount?.phone);
      return {
        ...values,
        phone: splits.phoneNumberWithoutPrefix,
        phonePrefixCountry: splits.phonePrefixCountry,
      };
    }
    return values;
  }, [
    country,
    currentAccount?.email,
    currentAccount?.firstName,
    currentAccount?.lastName,
    currentAccount?.phone,
  ]);

  const form = useFormik<WhatsappModalFormValues>({
    initialValues,
    onSubmit: onSubmitHandler,
    validationSchema,
  });

  return (
    <FormikProvider value={form}>
      <WhatsappModal
        breeder={formattedBreeder}
        pro={formattedPro}
        modal={modalHook?.modal}
        onConfirm={() => {
          form.submitForm();
        }}
      />
    </FormikProvider>
  );
};
